import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, useTheme, jsx, ClassNames } from '@emotion/react';
import { devWarning, uniqueId } from '@paypalcorp/pp-react-utils';
import { math } from 'polished';
import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo, forwardRef, useState, useContext, useCallback } from 'react';
import { IntlContext } from '@paypalcorp/pp-react-intl';
import { Flag } from '@paypalcorp/pp-react-flag';
import { MaskedTextInput } from '@paypalcorp/pp-react-masked-text-input';
import { DropdownMenu } from '@paypalcorp/pp-react-dropdown-menu';
var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }
  var sysBorderRadiusSm = tokens.sysBorderRadiusSm,
    sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
    sysSizeTapTargetDefault = tokens.sysSizeTapTargetDefault,
    sysSpaceMd = tokens.sysSpaceMd,
    sysSpaceSm = tokens.sysSpaceSm,
    sysSpaceXs = tokens.sysSpaceXs,
    sysSizeMediaXs = tokens.sysSizeMediaXs;
  var phoneInputMenuMaxWidth = '8.813rem';
  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: phone_input_base;\n    flex-grow: 1;\n  "])));
  var telephoneInputRtlStyles = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    input[type='tel'] {\n      label: tel_input_rtl;\n      direction: ltr;\n      text-align: right;\n    }\n  "])));
  var combined = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: combined;\n    display: flex;\n    gap: ", ";\n    [dir='rtl'] & {\n      direction: ltr;\n    }\n    @media screen and (max-width: ", ") {\n      display: block;\n    }\n  "])), sysSpaceSm, sysSizeMediaXs);
  var menuWidth = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: width;\n    min-width: ", ";\n    @media screen and (max-width: ", ") {\n      margin-bottom: ", ";\n    }\n    @media screen and (min-width: ", ") {\n      max-width: ", ";\n    }\n  "])), phoneInputMenuMaxWidth, sysSizeMediaXs, sysSpaceSm, math("".concat(sysSizeMediaXs, " + ").concat('0.0625rem')), phoneInputMenuMaxWidth);
  var menu = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: menu;\n    flex-grow: 1;\n    [dir='rtl'] & {\n      direction: rtl;\n    }\n  "])));
  var thumbnail = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: thumbnail;\n    margin-right: ", ";\n    [dir='rtl'] & {\n      margin-right: 0;\n      margin-left: ", ";\n    }\n  "])), sysSpaceSm, sysSpaceSm);
  var menuList = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: menu_list;\n    white-space: nowrap;\n  "])));
  var menuButton = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: menu_button;\n    flex-grow: 1;\n    &:not(:empty) {\n      padding-bottom: ", ";\n      padding-right: ", ";\n    }\n    & > span {\n      pointer-events: none;\n    }\n    [dir='rtl'] & {\n      border-radius: ", ";\n      margin-left: 0;\n      margin-right: 0;\n      &:not(:empty) {\n        padding-left: ", ";\n        padding-right: ", ";\n      }\n    }\n  "])), sysSpaceSm, math("".concat(sysSizeTapTargetDefault, " + ").concat(sysSpaceXs)), sysBorderRadiusSm, math("".concat(sysSizeTapTargetDefault, " + ").concat(sysSpaceXs)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessNormal)));
  var menuButtonContent = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: menu_button_content;\n    display: flex;\n  "])));
  var affordance = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: affordance;\n    margin-left: 0;\n    [dir='rtl'] & {\n      margin-right: 0;\n    }\n  "])));
  var menuContainer = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: container;\n    [dir='rtl'] & {\n      left: 0;\n    }\n  "])));
  return {
    base: base,
    combined: combined,
    menu: menu,
    thumbnail: thumbnail,
    menuList: menuList,
    menuButton: menuButton,
    menuButtonContent: menuButtonContent,
    affordance: affordance,
    telephoneInputRtlStyles: telephoneInputRtlStyles,
    menuWidth: menuWidth,
    menuContainer: menuContainer
  };
}

// 55 fixed line Countries with variable length
// 38 mobile line Countries with variable length
// 21 of those instersect
var VAR_LENGTH_COUNTRIES_FIXED_LINE = ['AT', 'AX', 'BD', 'BG', 'CD', 'CN', 'CU', 'CW', 'DE', 'DZ', 'EG', 'FI', 'GB', 'HR', 'ID', 'IE', 'IL', 'IQ', 'IR', 'IT', 'KE', 'KH', 'KI', 'KP', 'KR', 'LB', 'LR', 'LU', 'MM', 'MN', 'MU', 'MW', 'MY', 'NA', 'NG', 'PG', 'PH', 'PK', 'PL', 'PY', 'RO', 'RS', 'RW', 'SE', 'SH', 'SK', 'SO', 'SR', 'SY', 'TK', 'TW', 'VA', 'WS', 'YE', 'ZW'];
var VAR_LENGTH_COUNTRIES_MOBILE = ['AD', 'AR', 'AT', 'AX', 'BA', 'BG', 'BR', 'CD', 'CW', 'DE', 'EE', 'FI', 'GA', 'HR', 'ID', 'IS', 'IT', 'KH', 'KR', 'LB', 'LI', 'LR', 'MC', 'MM', 'MX', 'MY', 'NZ', 'PA', 'PN', 'RS', 'SB', 'SO', 'TK', 'TV', 'VA', 'WS', 'XK', 'ZA'];
var DEFAULT_PATTERN = '00000000000000000000';
// Get phone format from IntlProvider
function getPhoneFormat(intlContext, phoneRegion, formatType, isMobile) {
  var providedCountryCode = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : null;
  var providedPhoneFormat = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : '';
  var isPhoneMetaDataLoaded = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : true;
  if (providedCountryCode && providedPhoneFormat) {
    return {
      countryCode: providedCountryCode,
      pattern: providedPhoneFormat
    };
  } else if (phoneRegion && intlContext && intlContext.phoneFormatter) {
    var phoneType = isMobile ? 'MOBILE' : 'FIXED_LINE';
    var phoneExampleGenerator = intlContext.exampleGenerator({
      feature: 'phone',
      region: phoneRegion,
      type: phoneType
    });
    var exampleNumberObj = phoneExampleGenerator.generate();
    var countryCode = exampleNumberObj.countryCode,
      nationalNumber = exampleNumberObj.nationalNumber;
    var pattern = DEFAULT_PATTERN;
    if (isPhoneMetaDataLoaded) {
      var phoneNumberFormat = intlContext.phoneFormatter({
        phoneRegion: phoneRegion,
        style: formatType
      });
      var formattedNumber = phoneNumberFormat.format({
        countryCode: countryCode,
        nationalNumber: nationalNumber
      });
      var phoneNumber = formattedNumber;
      if (formattedNumber.includes('+')) {
        phoneNumber = formattedNumber.split("+".concat(countryCode))[1].trim();
      }
      pattern = phoneNumber.replace(/\d/g, '0');
      var varLengthCountries = isMobile ? VAR_LENGTH_COUNTRIES_MOBILE : VAR_LENGTH_COUNTRIES_FIXED_LINE;
      if (varLengthCountries.includes(phoneRegion)) {
        var patternLength = pattern.match(/0/g).length;
        var optionalDigits = '0'.repeat(20 - patternLength);
        pattern = pattern + "[".concat(optionalDigits, "]");
      }
    }
    return {
      countryCode: countryCode,
      pattern: pattern
    };
  }
  return {
    countryCode: providedCountryCode || '',
    pattern: providedPhoneFormat || DEFAULT_PATTERN
  };
}
var _excluded = ["rtl", "countryOptions", "options", "flagId", "codeId", "value"];
var PhoneCodeDropdown = function (_ref) {
  var rtl = _ref.rtl,
    countryOptions = _ref.countryOptions,
    options = _ref.options,
    flagId = _ref.flagId,
    codeId = _ref.codeId,
    value = _ref.value,
    rest = _objectWithoutProperties(_ref, _excluded);
  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  return jsx(DropdownMenu, _extends({
    renderButtonContent: function renderButtonContent() {
      var selectedOption; //istanbul ignore next

      if (value && !!options) {
        var selectedIndex = options.findIndex(function (opt) {
          return opt.value === value;
        });
        selectedOption = countryOptions[selectedIndex];
      }
      var countryPhoneCode = '';
      if (selectedOption && selectedOption.countryPhoneCode) {
        //istanbul ignore next
        if (rtl) {
          countryPhoneCode = "".concat(selectedOption.countryPhoneCode, "+");
        } else {
          countryPhoneCode = "+".concat(selectedOption.countryPhoneCode);
        }
      }
      if (selectedOption && selectedOption.phoneRegion) {
        return jsx("span", {
          css: _styles.menuButtonContent,
          "data-ppui": "true"
        }, jsx("span", {
          css: _styles.thumbnail,
          "data-ppui": "true"
        }, jsx(Flag, {
          id: flagId,
          countryCode: selectedOption.phoneRegion,
          size: 'md',
          "aria-label": selectedOption.countryName,
          role: "img",
          "data-ppui": "true"
        })), jsx("span", {
          id: codeId,
          "data-ppui": "true"
        }, countryPhoneCode));
      }
      return jsx(React.Fragment, null, countryPhoneCode);
    },
    options: options,
    value: value
  }, rest, {
    "data-ppui": "true"
  }));
};
var _excluded$1 = ["name", "countryOptions", "inputLabel", "menuLabel", "menuId", "className", "inputClassName", "menuClassName", "mobile", "onPhoneCodeChange", "phoneCode", "showUnselect", "unselectLabel", "formatType"];
var PhoneInputWithMenu = forwardRef(function (_ref, ref) {
  var _ref$name = _ref.name,
    name = _ref$name === void 0 ? 'phoneInputMenu' : _ref$name,
    _ref$countryOptions = _ref.countryOptions,
    countryOptions = _ref$countryOptions === void 0 ? [] : _ref$countryOptions,
    _ref$inputLabel = _ref.inputLabel,
    inputLabel = _ref$inputLabel === void 0 ? 'Phone Number' : _ref$inputLabel,
    _ref$menuLabel = _ref.menuLabel,
    menuLabel = _ref$menuLabel === void 0 ? 'Code' : _ref$menuLabel,
    menuId = _ref.menuId,
    className = _ref.className,
    inputClassName = _ref.inputClassName,
    menuClassName = _ref.menuClassName,
    _ref$mobile = _ref.mobile,
    mobile = _ref$mobile === void 0 ? false : _ref$mobile,
    onPhoneCodeChange = _ref.onPhoneCodeChange,
    phoneCode = _ref.phoneCode,
    _ref$showUnselect = _ref.showUnselect,
    showUnselect = _ref$showUnselect === void 0 ? false : _ref$showUnselect,
    _ref$unselectLabel = _ref.unselectLabel,
    unselectLabel = _ref$unselectLabel === void 0 ? 'None' : _ref$unselectLabel,
    _ref$formatType = _ref.formatType,
    formatType = _ref$formatType === void 0 ? 'international' : _ref$formatType,
    rest = _objectWithoutProperties(_ref, _excluded$1);

  /* istanbul ignore next */
  if (!countryOptions.length) {
    devWarning('Required prop `countryOptions` is missing.');
  }
  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isRtl = _useState2[0],
    setRtl = _useState2[1];
  var intlContext = useContext(IntlContext);
  var containerRef = useCallback(function (node) {
    if (node !== null && window && window.getComputedStyle(node)) {
      var computedStyle = window.getComputedStyle(node);
      setRtl(computedStyle && computedStyle.direction === 'rtl');
    }
  }, [intlContext === null || intlContext === void 0 ? void 0 : intlContext.locale]);
  var optionText;
  var optionCode;
  var optionPattern;
  var optionCountryRegion;
  var optionCountryName;
  var menuOptions;
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    hasFocus = _useState4[0],
    setFocus = _useState4[1];
  var constructOptionProps = function (countryName, countryPhoneCode, countryRegion, rtl) {
    optionText = countryName; // for none option don't need to show +

    /* istanbul ignore next */

    if (countryPhoneCode) {
      optionText += rtl ? " ".concat(countryPhoneCode, "+") : " +".concat(countryPhoneCode);
    }
    var optionValue = countryRegion && countryPhoneCode ? "".concat(countryRegion, "_").concat(countryPhoneCode) : '';
    return {
      primaryText: optionText,
      value: optionValue,
      decoration: countryRegion && jsx(Flag, {
        countryCode: countryRegion,
        size: "md",
        "data-ppui": "true"
      })
    };
  }; // Removing 'None' option if exists

  if (countryOptions[0] && countryOptions[0].phoneRegion === '') {
    countryOptions.splice(0, 1);
  }
  if (showUnselect) {
    // Adding 'None' option to list
    countryOptions.unshift({
      phoneRegion: '',
      countryName: unselectLabel,
      phoneFormat: DEFAULT_PATTERN
    });
  }
  menuOptions = useMemo(function () {
    return countryOptions.map(function (country, i) {
      var phoneParts = getPhoneFormat(intlContext, country.phoneRegion, formatType, mobile, country.countryPhoneCode, country.phoneFormat, country.isPhoneMetaDataLoaded);
      /* istanbul ignore next */

      var displayNamesObj = intlContext && intlContext.getDisplayNamesObject && intlContext.getDisplayNamesObject({
        type: 'region'
      });
      var countryCode = phoneParts.countryCode,
        pattern = phoneParts.pattern;
      optionCountryRegion = country.phoneRegion; //this one must be provided

      optionCode = country.countryPhoneCode || countryCode; //if provided use that

      optionPattern = country.phoneFormat || pattern; //if provided use that

      /* istanbul ignore next */

      optionCountryName = country.countryName || displayNamesObj && displayNamesObj.select(country.phoneRegion); //if provided use that

      countryOptions[i].countryPhoneCode = optionCode;
      countryOptions[i].phoneFormat = optionPattern;
      countryOptions[i].countryName = optionCountryName;
      return constructOptionProps(optionCountryName, optionCode, optionCountryRegion, isRtl);
    });
  }, [countryOptions, formatType, mobile]);
  var currentMask = DEFAULT_PATTERN;
  var selectedCountry = countryOptions.filter(function (country) {
    var selectedPhoneCode = country.phoneRegion && country.countryPhoneCode ? "".concat(country.phoneRegion, "_").concat(country.countryPhoneCode) : '';
    return phoneCode === selectedPhoneCode;
  });
  if (selectedCountry.length) {
    currentMask = selectedCountry[0].phoneFormat;
  }
  var handleChange = function (evt) {
    //istanbul ignore next
    if (onPhoneCodeChange) {
      var _evt$target, _evt$target$value;
      onPhoneCodeChange(evt); // determine if selected option is none/unselect
      // if it is don't move the focus to the next element

      if (!(((_evt$target = evt.target) === null || _evt$target === void 0 ? void 0 : (_evt$target$value = _evt$target.value) === null || _evt$target$value === void 0 ? void 0 : _evt$target$value.length) === 0 && showUnselect)) {
        setFocus(true);
      }
    }
  };
  var flagIdPrefix = 'phone-input-country-flag_';
  var codeIdPrefix = 'phone-input-country-code_';
  var generatedFlagId = menuId ? "".concat(flagIdPrefix).concat(menuId) : uniqueId(flagIdPrefix);
  var generatedCodeId = menuId ? "".concat(codeIdPrefix).concat(menuId) : uniqueId(codeIdPrefix);
  var phoneDropdownMenuCSS = [_styles.menu, _styles.menuWidth];
  var dropdownEl = jsx(ClassNames, {
    "data-ppui": "true"
  }, function (_ref2) {
    var css = _ref2.css;
    return jsx(PhoneCodeDropdown, {
      id: menuId,
      label: menuLabel,
      options: menuOptions,
      countryOptions: countryOptions,
      value: phoneCode,
      onChange: handleChange,
      css: phoneDropdownMenuCSS,
      className: menuClassName,
      menuContainerClassName: css(_styles.menuContainer),
      menuListClassName: css(_styles.menuList),
      menuButtonClassName: css(_styles.menuButton),
      sheetClassName: css(_styles.menuList),
      rtl: isRtl,
      showUnselect: true,
      flagId: generatedFlagId,
      codeId: generatedCodeId,
      "data-ppui": "true"
    });
  });
  var inputEl = jsx(ClassNames, {
    "data-ppui": "true"
  }, function (_ref3) {
    var cx = _ref3.cx;
    return jsx(MaskedTextInput, _extends({
      type: "tel",
      name: name,
      label: inputLabel,
      maskOptions: {
        mask: currentMask
      },
      css: _styles.menu,
      inputClassName: cx(inputClassName),
      setFocus: hasFocus,
      ref: ref,
      onBlur: function onBlur() {
        return setFocus(false);
      },
      "aria-describedby": "".concat(generatedFlagId, " ").concat(generatedCodeId)
    }, rest, {
      "data-ppui": "true"
    }));
  });
  var combinedInputCSS = [_styles.combined,
  //istanbul ignore next
  isRtl && _styles.telephoneInputRtlStyles];
  return jsx("div", {
    ref: containerRef,
    css: _styles.base,
    className: className,
    "data-ppui-info": "phone-input_2.7.6"
  }, jsx("div", {
    css: combinedInputCSS,
    "data-ppui": "true"
  }, dropdownEl, inputEl));
});
var _excluded$2 = ["name", "label", "countryOptions", "className", "inputClassName", "formatType", "mobile"];
var PhoneInput = forwardRef(function (_ref, ref) {
  var _ref$name = _ref.name,
    name = _ref$name === void 0 ? '' : _ref$name,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? 'Phone Number' : _ref$label,
    countryOptions = _ref.countryOptions,
    className = _ref.className,
    inputClassName = _ref.inputClassName,
    _ref$formatType = _ref.formatType,
    formatType = _ref$formatType === void 0 ? (countryOptions === null || countryOptions === void 0 ? void 0 : countryOptions.countryPhoneCode) ? 'international' : 'national' : _ref$formatType,
    _ref$mobile = _ref.mobile,
    mobile = _ref$mobile === void 0 ? false : _ref$mobile,
    rest = _objectWithoutProperties(_ref, _excluded$2);

  /* istanbul ignore next */
  if (!countryOptions) {
    devWarning('Required prop `countryOptions` is missing.');
  }
  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isRtl = _useState2[0],
    setRtl = _useState2[1];
  var intlContext = useContext(IntlContext);
  var containerRef = useCallback(function (node) {
    if (node !== null && window && window.getComputedStyle(node)) {
      var computedStyle = window.getComputedStyle(node);
      setRtl(computedStyle && computedStyle.direction === 'rtl');
    }
  }, [intlContext === null || intlContext === void 0 ? void 0 : intlContext.locale]);
  var phoneParts = useMemo(function () {
    return getPhoneFormat(intlContext, countryOptions === null || countryOptions === void 0 ? void 0 : countryOptions.phoneRegion, formatType, mobile, countryOptions === null || countryOptions === void 0 ? void 0 : countryOptions.countryPhoneCode, countryOptions === null || countryOptions === void 0 ? void 0 : countryOptions.phoneFormat, countryOptions === null || countryOptions === void 0 ? void 0 : countryOptions.isPhoneMetaDataLoaded);
  }, [countryOptions === null || countryOptions === void 0 ? void 0 : countryOptions.phoneRegion, formatType, mobile]);
  var countryCode = phoneParts.countryCode,
    pattern = phoneParts.pattern;
  var codePrefix = formatType === 'international' ? countryCode : '';
  var codeSuffix = codePrefix;
  if (formatType === 'international') {
    //istanbul ignore next
    codePrefix = isRtl ? '' : "+".concat(codePrefix); //istanbul ignore next

    codeSuffix = isRtl ? "".concat(codeSuffix, "+") : '';
  }
  return jsx("div", {
    ref: containerRef,
    css: [_styles.base, isRtl && _styles.telephoneInputRtlStyles],
    className: className,
    "data-ppui-info": "phone-input_2.7.6"
  }, jsx(ClassNames, {
    "data-ppui": "true"
  }, function (_ref2) {
    var cx = _ref2.cx;
    return jsx(MaskedTextInput, _extends({
      type: "tel",
      name: "phoneInput-".concat(name),
      label: label,
      prefix: codePrefix,
      suffix: codeSuffix,
      ref: ref,
      maskOptions: {
        mask: pattern
      },
      inputClassName: cx(inputClassName)
    }, rest, {
      "data-ppui": "true"
    }));
  }));
});
export { PhoneInput, PhoneInputWithMenu, styles as phoneInputStyles };